/**
 * Her birth assets (spec §4.2, §7): the int8 sem table and model.json, shipped
 * as one base64 JSON chunk keyed by the bundled graph. Loaded lazily after the
 * graph (spec §8) and checked against the loaded vocabulary before use.
 */
import {
  checkModel,
  decodeSem,
  inDegrees,
  sortedVocab,
  type AriadneDeps,
  type AriadneIds,
  type GraphData,
} from "@synapse/engine";

export interface AriadneAssetJson {
  graph_hash: string;
  vocab_hash: string;
  ariadne_hash: string;
  d: number;
  n: number;
  model: unknown;
  sem_b64: string;
}

export interface AriadneAssets {
  deps: AriadneDeps;
  ids: AriadneIds;
  n: number;
}

const B64 = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";

/** Base64 → bytes with no Buffer dependency (RN has none; a loop is enough). */
export function base64ToBytes(s: string): Uint8Array {
  const clean = s.replace(/[=]+$/, "");
  const out = new Uint8Array(Math.floor((clean.length * 3) / 4));
  let acc = 0;
  let bits = 0;
  let j = 0;
  for (let i = 0; i < clean.length; i++) {
    const v = B64.indexOf(clean[i]);
    if (v < 0) throw new Error("ariadne assets: bad base64");
    acc = ((acc << 6) | v) & 0xffffff;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out[j++] = (acc >> bits) & 0xff;
    }
  }
  return out;
}

export function assembleAssets(
  json: AriadneAssetJson,
  graph: GraphData,
  vocabHash: string | null,
): AriadneAssets {
  if (vocabHash && json.vocab_hash !== vocabHash) {
    throw new Error(
      `ariadne assets are for vocabulary ${json.vocab_hash.slice(0, 12)}, not ${vocabHash.slice(0, 12)}`,
    );
  }
  const words = sortedVocab(graph);
  if (words.length !== json.n) {
    throw new Error(
      `ariadne assets: ${json.n} words, graph has ${words.length}`,
    );
  }
  const sem = decodeSem(base64ToBytes(json.sem_b64), words, json.d);
  const model = checkModel(json.model);
  const indeg = inDegrees(graph, sem);
  return {
    deps: { graph, sem, model, indeg },
    ids: {
      vocab_hash: json.vocab_hash,
      graph_hash: json.graph_hash,
      ariadne_hash: json.ariadne_hash,
    },
    n: words.length,
  };
}

/** The lazy chunk (webpack cacheGroup `ariadne-data`; Metro bundles the JSON). */
export async function loadAriadneAssets(
  graph: GraphData,
  vocabHash: string | null,
): Promise<AriadneAssets> {
  const mod = await import("../data/ariadne.json");
  const json = ((mod as { default?: unknown }).default ??
    mod) as AriadneAssetJson;
  return assembleAssets(json, graph, vocabHash);
}
